import React, { useId } from 'react'

import type { SuperSource } from '@slite/search-client'

import {
  DEFAULT_SOURCE_TYPES,
  ORBITE_SIZE_PRESETS,
  STATIC_ORBIT_CONFIGS,
} from './constants'
import { OrbiteItem } from './OrbiteItem'

type OrbitProps = {
  radius: number
  sourceTypes: (SuperSource | 'plus-count')[]
}

const MAX_VISIBLE_ITEMS = 3
const ANGLE_RANGE = Math.PI * 0.9
const ANGLE_START = Math.PI * 0.25

const roundCoordinate = (value: number): number => Number(value.toFixed(6))

const calculateItemPosition = (
  itemIndex: number,
  totalItems: number,
  orbitConfig: (typeof STATIC_ORBIT_CONFIGS)[0],
  orbitIndex: number,
  centerX: number,
  centerY: number
) => {
  const { radius, isInnermost } = orbitConfig
  const radiusX = radius
  const radiusY = isInnermost ? radius : radius * 0.55
  const rotation = isInnermost ? 0 : -35

  const angleStep = ANGLE_RANGE / Math.max(1, totalItems - 1)
  const angle = ANGLE_START + itemIndex * angleStep

  const ellipseX = radiusX * Math.cos(angle)
  const ellipseY = radiusY * Math.sin(angle)

  if (isInnermost) {
    return {
      x: roundCoordinate(centerX + radius * Math.cos(angle) - 10),
      y: roundCoordinate(centerY + radius * Math.sin(angle) - 60),
    }
  }

  const isManuallyPositioned = orbitIndex % 2 === 0
  const rotRad = (rotation * Math.PI) / (isManuallyPositioned ? 120 : 200)

  if (isManuallyPositioned) {
    return {
      x: roundCoordinate(
        centerX +
          (ellipseX - 40 * Math.cos(rotRad) - ellipseY * Math.sin(rotRad))
      ),
      y: roundCoordinate(
        centerY + (ellipseX * Math.sin(rotRad) + ellipseY * Math.cos(rotRad))
      ),
    }
  }

  return {
    x: roundCoordinate(
      centerX + (ellipseX * Math.cos(rotRad) - ellipseY * Math.sin(rotRad))
    ),
    y: roundCoordinate(
      centerY + (ellipseX * Math.sin(rotRad) + ellipseY * Math.cos(rotRad))
    ),
  }
}

const distributeItemsAcrossOrbits = (items: SuperSource[]): OrbitProps[] => {
  const orbits: OrbitProps[] = STATIC_ORBIT_CONFIGS.map((config) => ({
    radius: config.radius,
    sourceTypes: [],
  }))

  items.forEach((item, index) => {
    const orbitIndex = 3 - (index % 4)
    orbits[orbitIndex].sourceTypes.push(item)
  })

  return orbits
}

const OrbitRing: React.FC<{
  orbit: OrbitProps
  orbitIndex: number
  centerX: number
  centerY: number
  extraCount: number
  totalOrbits: number
  showEllipseOnly?: boolean
  showCirclesOnly?: boolean
}> = ({
  orbit,
  orbitIndex,
  centerX,
  centerY,
  extraCount,
  totalOrbits,
  showEllipseOnly = false,
  showCirclesOnly = false,
}) => {
  const orbitConfig = STATIC_ORBIT_CONFIGS[orbitIndex]
  const { radius, isInnermost } = orbitConfig
  const radiusX = radius
  const radiusY = isInnermost ? radius : radius * 0.55
  const rotation = isInnermost ? 0 : -35

  return (
    <g key={orbitIndex}>
      {!showCirclesOnly && (
        <ellipse
          cx={centerX}
          cy={centerY}
          rx={radiusX}
          ry={radiusY}
          fill="none"
          stroke="currentColor"
          strokeDasharray="2 4"
          strokeWidth="1"
          opacity="0.4"
          transform={`rotate(${rotation} ${centerX} ${centerY})`}
        />
      )}

      {!showEllipseOnly &&
        orbit.sourceTypes.map((orbitSource, itemIndex) => {
          const { x, y } = calculateItemPosition(
            itemIndex,
            orbit.sourceTypes.length,
            orbitConfig,
            orbitIndex,
            centerX,
            centerY
          )

          const isPlusCount = orbitSource === 'plus-count'
          const isMainItem = orbitIndex === totalOrbits - 1

          return (
            <OrbiteItem
              key={`${orbitSource}-${orbitIndex}-${itemIndex}`}
              sourceType={isPlusCount ? undefined : orbitSource}
              x={x}
              y={y}
              size={isMainItem ? 'large' : 'medium'}
              label={isPlusCount ? `+${extraCount}` : undefined}
              withBackground={false}
            />
          )
        })}
    </g>
  )
}

const WIDTH = 430
const HEIGHT = 280

export const StaticOrbiteScene: React.FC<{
  className?: string
  sourceTypes?: SuperSource[]
  style?: React.CSSProperties
}> = ({
  className = 'shade-05',
  style,
  sourceTypes = DEFAULT_SOURCE_TYPES,
}) => {
  const [isMounted, setIsMounted] = React.useState(false)

  React.useEffect(() => {
    setIsMounted(true)
  }, [])

  const centerX = WIDTH / 2
  const centerY = HEIGHT / 2

  const visibleItems = sourceTypes.slice(0, MAX_VISIBLE_ITEMS)
  const extraCount = Math.max(0, sourceTypes.length - MAX_VISIBLE_ITEMS)

  const orbits = distributeItemsAcrossOrbits(visibleItems)

  if (extraCount > 0) {
    orbits[0].sourceTypes.push('plus-count')
  }

  const maskId = useId()
  const gradientId = useId()
  const ellipseMaskId = useId()

  if (!isMounted) {
    return (
      <div
        className={className}
        style={{ width: WIDTH, height: HEIGHT, ...style }}
      />
    )
  }

  const generateEllipseMask = () => {
    const circles: React.ReactElement[] = []

    orbits.forEach((orbit, orbitIndex) => {
      const orbitConfig = STATIC_ORBIT_CONFIGS[orbitIndex]

      orbit.sourceTypes.forEach((orbitSource, itemIndex) => {
        const { x, y } = calculateItemPosition(
          itemIndex,
          orbit.sourceTypes.length,
          orbitConfig,
          orbitIndex,
          centerX,
          centerY
        )

        const isMainItem = orbitIndex === orbits.length - 1
        const preset = ORBITE_SIZE_PRESETS[isMainItem ? 'large' : 'medium']

        circles.push(
          <circle
            key={`mask-circle-${orbitIndex}-${itemIndex}`}
            cx={x}
            cy={y}
            r={preset.circleRadius}
            fill="black"
          />
        )
      })
    })

    return circles
  }

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="5%" stopColor="white" stopOpacity="0" />
          <stop offset="40%" stopColor="white" stopOpacity="1" />
          <stop offset="100%" stopColor="white" stopOpacity="1" />
        </linearGradient>
        <mask id={maskId}>
          <rect width="100%" height="100%" fill={`url(#${gradientId})`} />
        </mask>

        <mask id={ellipseMaskId}>
          <rect width="100%" height="100%" fill="white" />
          {generateEllipseMask()}
        </mask>
      </defs>

      <g mask={`url(#${maskId})`}>
        {orbits.map((orbit, orbitIndex) => (
          <g key={orbitIndex} mask={`url(#${ellipseMaskId})`}>
            <OrbitRing
              orbit={orbit}
              orbitIndex={orbitIndex}
              centerX={centerX}
              centerY={centerY}
              extraCount={extraCount}
              totalOrbits={orbits.length}
              showEllipseOnly={true}
            />
          </g>
        ))}

        {orbits.map((orbit, orbitIndex) => (
          <OrbitRing
            key={`circles-${orbitIndex}`}
            orbit={orbit}
            orbitIndex={orbitIndex}
            centerX={centerX}
            centerY={centerY}
            extraCount={extraCount}
            totalOrbits={orbits.length}
            showCirclesOnly={true}
          />
        ))}
      </g>
    </svg>
  )
}
